import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {RoutesAddonsEnums, RoutesEnums} from '../../../enums/RoutesEnums';
import {SearchService} from '../../../services/search/search.service';
import {SearchWidgetDataItem} from '../../../types/SearchWidgetDataItem';
import {RelatedLink} from '../types/RelatedLink';
import {RelatedCategoryConfigService} from './related-category-config.service';

@Injectable({
  providedIn: 'root',
})
export class RelatedLinksService {
  private searchService = inject(SearchService);
  private configService = inject(RelatedCategoryConfigService);

  /**
   * Generate category links for the main category page
   */
  public generateCategoryLinks(
    categoryType: RoutesAddonsEnums,
    widgetDataItems: SearchWidgetDataItem[],
  ): RelatedLink[] {
    if (!widgetDataItems || !categoryType) {
      return [];
    }

    const config = this.configService.getCategoryConfig(categoryType);
    const links: RelatedLink[] = [];

    widgetDataItems.forEach((item) => {
      const itemUrlPathWithAddon = `${item.urlPath}-${config.addon}`;
      const link: RelatedLink = {
        name: item.name,
        routerLink: ['/', RoutesEnums.SEARCH, itemUrlPathWithAddon],
        title: `Hitta ${item.name}`,
        moreRouterLink: ['/', RoutesEnums.RELATED, categoryType, itemUrlPathWithAddon],
        moreTitle: `Relaterade sökningar på ${item.name}`,
      };
      links.push(link);
    });

    return links;
  }

  /**
   * Generate detailed category links for the category details page
   */
  public generateDetailedCategoryLinks(
    categoryType: RoutesAddonsEnums,
    selectedItem: SearchWidgetDataItem,
    places: any,
  ): RelatedLink[] {
    if (!selectedItem || !places || !categoryType) {
      return [];
    }

    const config = this.configService.getCategoryConfig(categoryType);
    const selectedItemUrlPathWithAddon = `${selectedItem.urlPath}-${config.addon}`;
    const links: RelatedLink[] = [];

    places.places
      .filter((item: any) => item.noFollow === false)
      .forEach((item: any) => {
        const itemUrlPathWithAddon = `${item.urlPath}-p`;
        const link: RelatedLink = {
          name: item.name,
          routerLink: ['/', RoutesEnums.SEARCH, selectedItemUrlPathWithAddon, itemUrlPathWithAddon],
          title: `Hitta ${selectedItem.name} i ${item.name}`,
          caregiverUnitsCount: item.caregiverUnitsCount,
        };
        links.push(link);

        // Add child items if they exist
        if (item.children) {
          item.children.forEach((childItem: any) => {
            const childItemUrlPathWithAddon = `${childItem.urlPath}-p`;
            const childLink: RelatedLink = {
              name: childItem.name,
              routerLink: ['/', RoutesEnums.SEARCH, selectedItemUrlPathWithAddon, childItemUrlPathWithAddon],
              title: `Hitta ${selectedItem.name} i ${childItem.name}`,
              caregiverUnitsCount: childItem.caregiverUnitsCount,
            };
            links.push(childLink);
          });
        }
      });

    return links;
  }

  /**
   * Get widget data for a specific category type
   */
  public getWidgetDataForCategory(categoryType: RoutesAddonsEnums): Observable<SearchWidgetDataItem[]> {
    const config = this.configService.getCategoryConfig(categoryType);
    return this.searchService.getWidgetData(config.widgetDataType);
  }

  /**
   * Find a specific item by URL path from widget data
   */
  public findItemByUrlPath(widgetDataItems: SearchWidgetDataItem[], urlPath: string): SearchWidgetDataItem | null {
    if (!widgetDataItems || !urlPath) {
      return null;
    }
    return widgetDataItems.find((item) => item.urlPath === urlPath) || null;
  }
}
